import {Button} from '@kit';

import React, {Suspense, lazy, useCallback, useEffect, useMemo, useState} from 'react';

import {
	CommentTargetType,
	CommentsDocument,
	CommentsQuery,
	useBlockedCommentAuthorsQuery,
	useCommentsQuery,
	useCreateCommentMutation,
	useDeleteCommentMutation,
	useSetCommentsBlockedMutation,
	useUpdateCommentMutation,
} from '../../generated/types';
import {Link} from '../../lib/routes';
import {useUser} from '../use-user';
import {useToggle} from '../utils/hooks';
import {CommentForm} from './comment-form';
import {CommentList} from './comment-item';
import {buildCommentTree} from './comment-tree';
import {useNow} from './use-now';
import {CommentsContext, CommentsContextValue} from './comments-context';

import css from './comments.module.css';

const LoginPopup = lazy(() => import('../login-popup').then((m) => ({default: m.LoginPopup})));

type Props = {
	targetType: CommentTargetType;
	/** id страницы, к которой относятся комментарии; у старой редакции — id новой */
	targetId: string;
	className?: string;
};

/** сбой мутации приходит в поле error, а не исключением — поднимаем его, чтобы форма показала текст */
function unwrap<T extends {error?: unknown}>(result: T): T {
	if (result.error) throw result.error;
	return result;
}

export const Comments: React.FC<Props> = ({targetType, targetId, className}) => {
	const {id: userId, isAdmin, isCommentsBlocked, loading: userLoading} = useUser();
	const now = useNow();
	const [ready, setReady] = useState(false);
	const [openLogin, toggleLogin] = useToggle(false);
	useEffect(() => setReady(true), []);

	const variables = useMemo(() => ({targetType, targetId}), [targetType, targetId]);
	const {data} = useCommentsQuery({ssr: true, variables});
	const {data: blockedData, refetch: refetchBlocked} = useBlockedCommentAuthorsQuery({
		variables,
		skip: !isAdmin,
	});

	const [createComment] = useCreateCommentMutation();
	const [updateComment] = useUpdateCommentMutation();
	const [deleteComment] = useDeleteCommentMutation();
	const [setCommentsBlocked] = useSetCommentsBlockedMutation();

	const comments = data?.comments;
	const tree = useMemo(() => buildCommentTree(comments || []), [comments]);
	const count = useMemo(() => (comments || []).filter((comment) => !comment.isDeleted).length, [comments]);
	const blockedAuthors = useMemo(() => new Set(blockedData?.blockedCommentAuthors || []), [blockedData]);

	const create = useCallback(
		(text: string, parentId?: string) =>
			createComment({
				variables: {...variables, text, parentId},
				update: (cache, {data: created}) => {
					if (!created?.createComment) return;
					cache.updateQuery<CommentsQuery>({query: CommentsDocument, variables}, (prev) =>
						prev ? {comments: [...prev.comments, created.createComment]} : prev,
					);
				},
			}).then(unwrap),
		[createComment, variables],
	);

	const value = useMemo<CommentsContextValue>(
		() => ({
			canWrite: !!userId && !isCommentsBlocked,
			isAdmin: !!isAdmin,
			now,
			userId,
			blockedAuthors,
			reply: (parentId, text) => create(text, parentId),
			edit: (id, text) => updateComment({variables: {id, text}}).then(unwrap),
			remove: (id) =>
				deleteComment({
					variables: {id},
					update: (cache) => {
						cache.modify({
							id: cache.identify({__typename: 'Comment', id}),
							fields: {isDeleted: () => true, text: () => ''},
						});
					},
				}).then(unwrap),
			setBlocked: (blockedUserId, blocked) =>
				setCommentsBlocked({variables: {userId: blockedUserId, blocked}})
					.then(unwrap)
					.then(() => refetchBlocked()),
		}),
		[
			userId,
			isAdmin,
			now,
			isCommentsBlocked,
			blockedAuthors,
			create,
			updateComment,
			deleteComment,
			setCommentsBlocked,
			refetchBlocked,
		],
	);

	const handleCreate = useCallback((text: string) => create(text), [create]);

	const footer = !ready || userLoading ? null : !userId ? (
		<div className={css.notice}>
			Чтобы оставить комментарий, <Button link onClick={toggleLogin}>войдите</Button> или{' '}
			<Link route="registration">зарегистрируйтесь</Link>.
		</div>
	) : isCommentsBlocked ? (
		<div className={css.notice}>Администратор закрыл вам возможность писать комментарии.</div>
	) : (
		<CommentForm key={count} submitLabel="Отправить" onSubmit={handleCreate} />
	);

	return (
		<section className={className ? `${css.comments} ${className}` : css.comments} aria-labelledby="comments-title" data-print-hide>
			{openLogin && (
				<Suspense fallback={null}>
					<LoginPopup onClose={toggleLogin} />
				</Suspense>
			)}
			<h2 id="comments-title" className={css.title}>
				Комментарии{count > 0 && <span className={css.count}>{count}</span>}
			</h2>
			<CommentsContext.Provider value={value}>
				{tree.length > 0 ? (
					<CommentList nodes={tree} depth={0} />
				) : (
					data && <div className={css.empty}>Комментариев пока нет.</div>
				)}
				<div className={css.footer}>{footer}</div>
			</CommentsContext.Provider>
		</section>
	);
};
